import {ProductTechnicalPropertyTypeIds} from "../../../../../../../../core/constants/enums";
import {ImportingDataTypes} from "../../types";
import {createInvalidPartSection, PreventativeAutocompleteType, PreventionInvalidityCode} from "./shared";

const partNumberField = {
    key: 'partNumber',
    label: 'Part Number',
    type: 'text',
    skippable: false,
    extractValue: (o) => (typeof o === 'string' ? o : o?.partNumber ?? o?.partNo ?? null),
};

/**
 * @param {string} fieldKey e.g. property_123
 * @param {Array<{name: string, title: string}>} mappingFields
 * @param {'predefinedOptions'|'missingMappedProperties'} metaKey
 */
const createTechnicalPredefinedPropertyField = (fieldKey, mappingFields, metaKey) => {
    const mapped = mappingFields?.find((f) => f.name === fieldKey);
    const propertyId = fieldKey.replace(/^property_/, '');

    return {
        key: fieldKey,
        label: mapped?.title ?? fieldKey,
        skipTarget: fieldKey,
        skippable: true,
        type: PreventativeAutocompleteType.INDUSTRY_PROPERTY_OPTIONS,
        appliesToIssue: (issue) => !!issue?.meta?.[metaKey]?.[fieldKey],
        extractValue: (val, issue) => {
            const isMulti = !!issue?.meta?.[metaKey]?.[fieldKey]?.isMultiSelect;
            if (Array.isArray(val)) {
                return val.map((o) => o?.title ?? '').filter(Boolean).join(',');
            }
            if (val && typeof val === 'object' && val.title != null) {
                return val.title;
            }
            return isMulti ? (val ?? '') : (val ?? '');
        },
        modifyRow: (row, val) => ({...row, [fieldKey]: val}),
        autocompletePropsFromIssue: (issue, staticDataContext) => {
            const meta = issue?.meta?.[metaKey]?.[fieldKey] ?? {};
            const options = staticDataContext?.propertyOptionsByPropertyId?.[String(propertyId)] ?? [];
            return {
                propertyId: meta.propertyId ?? propertyId,
                cachedOptions: options,
                multiple: !!meta.isMultiSelect,
                getOptionLabel: (o) => o?.title ?? '',
                isOptionEqualToValue: (o, v) => o?.id === v?.id,
            };
        },
    };
};

/**
 * @param {string} fieldKey
 * @param {Array<{name: string, title: string}>} mappingFields
 */
const createPredefinedPropertyField = (fieldKey, mappingFields) =>
    createTechnicalPredefinedPropertyField(fieldKey, mappingFields, 'predefinedOptions');

/**
 * @param {string} fieldKey
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 */
const createMissingMappedPropertyField = (fieldKey, issues, mappingFields) => {
    const metaEntry = issues
        .map((issue) => issue?.meta?.missingMappedProperties?.[fieldKey])
        .find(Boolean);
    const mapped = mappingFields?.find((f) => f.name === fieldKey);
    const label = metaEntry?.propertyTitle ?? mapped?.title ?? fieldKey;

    if (metaEntry?.hasPredefined) {
        return createTechnicalPredefinedPropertyField(fieldKey, mappingFields, 'missingMappedProperties');
    }

    if (metaEntry?.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.boolean) {
        return {
            key: fieldKey,
            label,
            skipTarget: fieldKey,
            skippable: true,
            type: 'toggle',
            appliesToIssue: (issue) => !!issue?.meta?.missingMappedProperties?.[fieldKey],
            modifyRow: (row, val) => ({...row, [fieldKey]: val}),
        };
    }

    return {
        key: fieldKey,
        label,
        skipTarget: fieldKey,
        skippable: true,
        type: 'input',
        appliesToIssue: (issue) => !!issue?.meta?.missingMappedProperties?.[fieldKey],
        modifyRow: (row, val) => ({...row, [fieldKey]: val}),
    };
};

/**
 * @param {string} fieldKey
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 */
const createMultiselectNotSupportedPropertyField = (fieldKey, issues, mappingFields) => {
    const metaEntry = issues
        .map((issue) => issue?.meta?.multiselectNotSupported?.[fieldKey])
        .find(Boolean);
    const mapped = mappingFields?.find((f) => f.name === fieldKey);

    return {
        key: fieldKey,
        label: metaEntry?.propertyTitle ?? mapped?.title ?? fieldKey,
        skipTarget: fieldKey,
        skippable: true,
        type: 'text',
        requiresAttention: true,
        appliesToIssue: (issue) => !!issue?.meta?.multiselectNotSupported?.[fieldKey],
        extractValue: (_o, issue) => issue?.meta?.multiselectNotSupported?.[fieldKey]?.rawValue ?? '',
    };
};

/**
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 * @returns {import('./shared/section-factories').PreventativeField[]}
 */
export const buildMissingMappedPropertyFields = (issues, mappingFields) => {
    const fieldKeys = [
        ...new Set(
            issues.flatMap((issue) => Object.keys(issue?.meta?.missingMappedProperties ?? {})),
        ),
    ].sort();

    return [
        partNumberField,
        ...fieldKeys.map((key) => createMissingMappedPropertyField(key, issues, mappingFields)),
    ];
};

/**
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 * @returns {import('./shared/section-factories').PreventativeField[]}
 */
export const buildInvalidPredefinedOptionFields = (issues, mappingFields) => {
    const fieldKeys = [
        ...new Set(
            issues.flatMap((issue) => Object.keys(issue?.meta?.predefinedOptions ?? {})),
        ),
    ].sort();
    return [partNumberField, ...fieldKeys.map((key) => createPredefinedPropertyField(key, mappingFields))];
};

/**
 * @param {Array<Object>} issues
 * @param {Array<{name: string, title: string}>} mappingFields
 */
export const buildMultiselectNotSupportedFields = (issues, mappingFields) => {
    const fieldKeys = [
        ...new Set(
            issues.flatMap((issue) => Object.keys(issue?.meta?.multiselectNotSupported ?? {})),
        ),
    ].sort();

    return [
        partNumberField,
        ...fieldKeys.map((key) => createMultiselectNotSupportedPropertyField(key, issues, mappingFields)),
    ];
};

/**
 * Preventative correction config for part technical information import.
 */
export const preventativeInteraction = {
    datasourceId: ImportingDataTypes.partTechnicalInformation,
    sections: [
        createInvalidPartSection(),
        {
            sectionKey: 'missingMappedPropertyValues',
            sectionTitle: 'Missing property values',
            invalidityCode: PreventionInvalidityCode.MISSING_REQUIRED_FIELDS,
            buildFields: buildMissingMappedPropertyFields,
        },
        {
            sectionKey: 'multiselectNotSupported',
            sectionTitle: 'Multi-select properties not supported',
            invalidityCode: PreventionInvalidityCode.MULTISELECT_NOT_SUPPORTED,
            buildFields: buildMultiselectNotSupportedFields,
        },
        {
            sectionKey: 'invalidPredefinedOption',
            sectionTitle: 'Invalid predefined property values',
            invalidityCode: PreventionInvalidityCode.INVALID_PREDEFINED_OPTION,
            buildFields: buildInvalidPredefinedOptionFields,
        },
    ],
};
